import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { ComboBox, MultiSelect } from '@progress/kendo-react-dropdowns';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { callApiWithAuthorization } from '../../../API/CallApi';
import EntityRecommendationContext, { EntityRecommendationContextProvider } from '../../../Context/EntityRecommendation/EntityRecommendationContext';
import TYPES from '../../../Context/EntityRecommendation/TYPES';
import RecommendedLibraryProjects from '../../Dashboard/SolutionLibrary/Recommendation/RecommendedLibraryProject';
import EntityRelationsRecommendation, { RecommendedLinkedEntityGrid } from '../../LinkedEntities/Recommendation/EntityRelationsRecommendation';
import Scrollable from '../../Shared/Edit/Scrollable';
import useImportSelectedRecommendationData from '../../../Hooks/Recommendation/useImportSelectedRecommendationData';
import { useMsal } from '@azure/msal-react';
import RecommendationFilterMultiSelect from './RecommendationFilterMultiSelect';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import sortDataInSameOrderAsCustomEntities from '../../../Context/EntityRecommendation/sortDataInSameOrderAsCustomEntities';
import { Skeleton } from '@progress/kendo-react-indicators';
import { ENTITY_LABEL, ENTITY_PLURAL_LABEL } from '../../Helpers/Labels/EntityLabel';
import AttributeIcon from '../../Attributes/Fields/AttributeIcon';
import { ENTITY_RELATION_PLURAL_LABEL } from '../../Helpers/Labels/EntityRelationLabel';
import { OverlayLoader } from '../../Shared/Loader/OverlayLoader';
import InnerLoader from '../../Shared/Loader/InnerLoader';
import { Tooltip } from '@progress/kendo-react-tooltip';

const EntityRecommendation = ({
    entities = [],
    projectId, organizationId,
    onAdd, onEntityLimitReached, onTitleChange
}) => {

    const [currentTitle, setCurrentTitle] = useState(`Alice Recommendation - ${ENTITY_PLURAL_LABEL}`);

    const [showRecommendationGrid, setShowRecommendationGrid] = useState(true)
    const [showLinkedEntityGrid, setShowLinkedEntityGrid] = useState(false)
    const [showProjectTypeField, setShowProjectTypeField] = useState(false);
    const [showEntityTypeField, setShowEntityTypeField] = useState(false);
    const [showLibraryProjects, setShowLibraryProjects] = useState(false);

    const [projectType, setProjectType] = useState([ { text: "CRM" }, { text: "HR" } ]);
    const [entityType, setEntityType] = useState([]);

    const { importData, response, error } = useImportSelectedRecommendationData(
        organizationId, projectId
    )

    // everytime currentTitle changes, call onTitleChange to update dialog title
    useEffect(() => {
        if (onTitleChange) {
            onTitleChange(currentTitle);
        }
    }, [currentTitle]);
    
    // useEffect(() => {
       // getProjectTypes();
    // }, [])

    useEffect(() => {
        if (response && response.importedEntities) {
            onAdd(response.importedEntities);
        }
    }, [response])

    useEffect(() => {
        if (error && (
            error.message === "Max entities limit reached" ||
            error.message === "Max relations limit reached"
        )) {
            onEntityLimitReached();
        }
    }, [error])

    async function getProjectTypes() {
        const request = await callApiWithAuthorization("/api/v0/nodes?label=Domain");
        const response = await request.json();
        const domains = response.map(domain => ({
            text: domain.name,
            icon: domain.icon,
            id: domain.id,
            isSelected: false
        }))
        setProjectType(domains);
    }

    if (showRecommendationGrid) {
        return (
            <RecommendedEntityGrid
                entities={entities}
                projectType={projectType}
                fetchUsingUserInput={entities.length === 0}
                userInput={entityType}
                projectId={projectId}
                organizationId={organizationId}
                onEntityLimitReached={onEntityLimitReached}
                onNext={importData}
                addButtonText={"Add"} />
        )
    }

    if (showLinkedEntityGrid) {
        return (
            <RecommendedLinkedEntityGrid
                usedInWorkspace
                addButtonText={`Import ${ENTITY_PLURAL_LABEL}`}
                organizationId={organizationId}
                projectId={projectId}
                entities={entities}
                onAdd={importData}
                onBack={() => {
                    setShowLinkedEntityGrid(false);
                    setShowRecommendationGrid(true);
                }} />
        )
    }

    if (showProjectTypeField) {
        return <ProjectType
            types={projectType}
            value={projectType}
            onTypeChange={(selectedType) => {
                const newProjectTypeData = projectType.map(projectType => ({
                    ...projectType,
                    isSelected: projectType.id === selectedType.id
                }));
                setProjectType(newProjectTypeData)
            }}
            onNext={() => {
                const selectedProjectTypes = projectType.filter(type => type.isSelected);
                if (selectedProjectTypes.length === 0) {
                    setShowEntityTypeField(true);
                } else {
                    setShowLibraryProjects(true);
                }
                setShowProjectTypeField(false);
            }} />;
    }

    if (showLibraryProjects) {
        const tags = projectType.filter(type => type.isSelected).map(type => type.text)
        return (
            <>
                <RecommendedLibraryProjectsWithActions
                    onAppImported={onAdd}
                    organizationId={organizationId}
                    projectId={projectId}
                    onBack={() => {
                        setShowLibraryProjects(false);
                        setShowProjectTypeField(true);
                    }}
                    onNext={() => {
                        setShowLibraryProjects(false);
                        setShowEntityTypeField(true);
                    }}
                    tags={tags} />
            </>
        )
    }

    if (showEntityTypeField) {
        return (
            <EntityTypes
                names={entityType}
                onNameEntered={(e) => setEntityType(e.target.value)}
                onBack={() => {
                    const selectedProjectTypes = projectType.filter(type => type.isSelected);
                    if (selectedProjectTypes.length === 0) {
                        setShowProjectTypeField(true);
                    } else {
                        setShowLibraryProjects(true);
                    }
                    setShowEntityTypeField(false);
                }}
                onNext={() => {
                    setShowEntityTypeField(false);
                    setShowRecommendationGrid(true);
                }} />
        );
    }
};

export function ProjectType({
    types,
    allowCustomTags,
    customTags,
    handleCustomTags,
    onTypeChange,
    onBack,
    onNext
}) {

    return (
        <>
            <div>
                What app do you want to create today?
            </div>
            <div>
                If the app you want to create does not fall into any of these categories, you can type in the category.
            </div>
            <div className="container-fluid">
                {
                    allowCustomTags &&
                    <div className='row mt-2'>
                        <div className='col-12'>
                            <MultiSelect
                                allowCustom={true}
                                value={customTags}
                                onChange={handleCustomTags} />
                        </div>
                    </div>
                }
                <div className="row gap-2">
                    {
                        types.map(type => (
                            <div
                                className={
                                    "col-1 m-4 py-4 rounded-circle text-center pointer "
                                    + (type.isSelected ? "border-primary" : "border")
                                }
                                key={type.text}
                                onClick={() => onTypeChange(type)}
                            >
                                <h1
                                    className={type.icon} />
                                <div>
                                    <b>{ type.text }</b>
                                </div>
                            </div>
                        ))
                    }
                </div>
                <div className="row position-absolute bottom-10 right-10">
                    <div className='col-12 d-flex justify-content-between'>
                        {
                            onBack &&
                            typeof onBack === "function" &&
                            <button onClick={onBack} className="btn">Back</button>
                        }
                        <button className="btn btn-primary" onClick={onNext}>Next</button>
                    </div>
                </div>
            </div>
        </>
    )
}

export function EntityTypes({
    names,
    onNameEntered,
    onNext,
    onBack
}) {
    return (
        <>
            <div>
                <label htmlFor="entity-name-field">
                    Please tell me name of entities you might want.
                </label>
                <MultiSelect
                    id="entity-name-field"
                    allowCustom={true}
                    onChange={onNameEntered}
                    value={names}
                />
            </div>
            <div className="position-absolute bottom-10 right-10">
                <button onClick={onBack} className="btn">
                    Back
                </button>
                &nbsp;
                <button onClick={onNext} className="btn btn-primary">
                    Next
                </button>
            </div>
        </>
    )
}

export function RecommendedEntityGrid(props) {

    const {
        entities, fetchUsingUserInput, userInput, addButtonText = "Next",
        organizationId, projectType, usedInCreateProject,
        onNext, onBack, onEntitiesLoaded
    } = props;

    const {
        api, state, dispatch
    } = useContext(EntityRecommendationContext);

    const [customEntities, setCustomEntities] = useState([]);

    function getSelectedProjectTypes() {
        return projectType.filter(type => type.isSelected).map(type => type.text)
    }

    useEffect(() => {
        if (!state.entitiesLoaded) {
            getData()
        }
        if (!state.isShowMoreOptionVisible) {
            dispatch({
                type: TYPES.TOGGLE_SHOW_MORE_OPTION
            })
        }
    }, [])
    
    function getData() {
        if (fetchUsingUserInput) {
            const selectedProjectTypes = getSelectedProjectTypes();
            if (userInput.length > 0 ){
                api.getRecommendationUsingUserInput(
                    userInput.map(input => input.toLowerCase()),
                    organizationId,
                    selectedProjectTypes
                )
                    .then(data => {
                        dispatch({
                            type: TYPES.DATA_LOADED,
                            payload: data
                        })
                        if (data.length === 0) {
                            dispatch({
                                type: TYPES.TOGGLE_SHOW_MORE_OPTION
                            })
                        }
                    })
            } else {
                dispatch({ type: TYPES.COLD_START })
                api.getColdStartData(organizationId, selectedProjectTypes, 0, usedInCreateProject)
                    .then(data => {
                        dispatch({
                            type: TYPES.DATA_LOADED,
                            payload: data
                        })
                        if (data.length === 0) {
                            dispatch({
                                type: TYPES.TOGGLE_SHOW_MORE_OPTION
                            })
                        }
                    })
            }
        } else if (usedInCreateProject) {
            const selectedProjectTypes = getSelectedProjectTypes();
            api.getColdStartData(
                organizationId,
                selectedProjectTypes,
                0,
                usedInCreateProject
            )
                .then(data => {
                    if (data.length > 0) {
                        dispatch({
                            type: TYPES.ENTITY_RECOMMENDED
                        })
                    }
                    dispatch({
                        type: TYPES.DATA_LOADED,
                        payload: data
                    })
                    if (data.length === 0) {
                        dispatch({
                            type: TYPES.TOGGLE_SHOW_MORE_OPTION
                        })
                    }
                    if (onEntitiesLoaded) { onEntitiesLoaded(data); }
                })
        } else {
            api.getRecommendationUsingUserEntities(
                entities.map(entity => entity.name.toLowerCase()),
                organizationId,
                projectType
            )
                .then(async (data) => {
                    if (data.length > 0) {
                        dispatch({
                            type: TYPES.DATA_LOADED,
                            payload: data
                        })
                    } else {
                        const selectedProjectTypes = getSelectedProjectTypes();
                        dispatch({
                            type: TYPES.NO_ENTITY_RECOMMENDED,
                        })
                        dispatch({ type: TYPES.COLD_START })
                        const coldStartData = await api.getColdStartData(organizationId, selectedProjectTypes, 0, usedInCreateProject);
                        dispatch({
                            type: TYPES.DATA_LOADED,
                            payload: coldStartData
                        })
                    }
                    if (onEntitiesLoaded && typeof onEntitiesLoaded === "function") {
                        onEntitiesLoaded(data);
                    }
                    if (data.length === 0) {
                        dispatch({
                            type: TYPES.TOGGLE_SHOW_MORE_OPTION
                        })
                    }
                })
        }
    }

    const handleExpand = (event) => {
        dispatch({
            type: TYPES.ENTITY_EXPANDED,
            payload: { id: event.dataItem.id }
        })
    }

    const handleEntitySelect = (entity) => {
        const { id, selected } = entity;
        const type = selected ? TYPES.ENTITY_DESELECTED : TYPES.ENTITY_SELECTED;
        dispatch({
            type,
            payload: { id }
        })
    }

    const getNextSetOfEntities = async () => {
        const skip = state.data.length;
        const selectedProjectTypes = getSelectedProjectTypes();
        let nextEntities = [];
        if (state.isColdStart) {
            nextEntities = await api.getColdStartData(organizationId, selectedProjectTypes, skip, usedInCreateProject);
        } else if (fetchUsingUserInput) {
            const entityNames = userInput.map(input => input.toLowerCase());
            nextEntities = await api.getRecommendationUsingUserInput(
                entityNames, organizationId, selectedProjectTypes, skip
            )
        } else if (customEntities.length > 0) {
            const entityNames = customEntities.map(entityName => entityName.toLowerCase());
            nextEntities = await api.getRecommendationUsingUserInput(
                entityNames, organizationId, selectedProjectTypes, skip
            )
        } else {
            const entityNames = entities.map(entity => entity.name.toLowerCase());
            nextEntities = await api.getRecommendationUsingUserEntities(
                entityNames,
                organizationId,
                selectedProjectTypes,
                skip
            )
        }
        dispatch({
            type: TYPES.NEXT_ENTITY_SET_LOADED,
            payload: nextEntities
        })
        if (nextEntities.length === 0) {
            dispatch({
                type: TYPES.TOGGLE_SHOW_MORE_OPTION,
            })
        }
    }

    async function refreshData() {
        dispatch({
            type: TYPES.START_LOADING
        });
        const selectedProjectTypes = getSelectedProjectTypes();
        if (customEntities.length === 0) {
            getData();
        } else {
            const data = await api.getRecommendationUsingUserInput(
                customEntities.map(entity => entity.name.toLowerCase()),
                organizationId,
                selectedProjectTypes
            )
            dispatch({
                type: TYPES.ENTITY_RECOMMENDED
            })
            const newCustomEntities = setHasNoMatchForNewData(customEntities, data);
            setCustomEntities(newCustomEntities)
            const selectedData = sortDataInSameOrderAsCustomEntities(data, newCustomEntities).map(entity => ({
                ...entity,
                selected: newCustomEntities.some(customEntity => (
                    customEntity.name.toLowerCase() === entity.name.toLowerCase()
                ))
            }));
            // set selected to true for first 20 attributes of each selected entity in selectedData
            const selectedDataWithAttributes = selectedData.map(entity => {
                debugger;
                const attributes = entity.attributes.map((attribute, index) => {
                    return {
                        ...attribute,
                        selected: entity.selected && index < 20
                    }
                })
                return {
                    ...entity,
                    attributes
                }
            })
            dispatch({
                type: TYPES.DATA_LOADED,
                payload: selectedDataWithAttributes
            })
            if (data.length === 0 && state.isShowMoreOptionVisible) {
                dispatch({
                    type: TYPES.TOGGLE_SHOW_MORE_OPTION
                })
            }
        }
    }

    // a function which sets hasNoMatch property in each value
    // against another variable passed in argument
    function setHasNoMatchForNewData(values, data) {
        return values.map(value => {
            const hasNoMatch = !data.find(entity => entity.name.toLowerCase() === value.name.toLowerCase());
            return {
                name: value.name,
                hasNoMatch
            }
        })
    }

    const handleCustomEntityChange = (event) => {
        // get values from event
        const { value } = event.target;

        // set a hasNoMatch property in each value
        // if an entity with same name is not found in state.data
        const values = value.map((value) => {
            const hasNoMatch = !state.data.find(entity => entity.name.toLowerCase() === value.name.toLowerCase());
            return {
                name: value.name,
                hasNoMatch
            }
        }).splice(0, 10);

        // set value of custom entities
        setCustomEntities(values);

        // dispatch action to update state
        dispatch({
            type: TYPES.CUSTOM_ENTITIES_CHANGED,
            payload: values
        });
    }

    function handleSelectAll() {
        // dispatch action to select all entities
        dispatch({
            type: TYPES.TOGGLE_SELECT_ALL_ENTITIES
        })
    }

    function shouldNextBeDisabled() {
        if (usedInCreateProject) {
            return state.loading;
        } else {
            return (
                !state.data.some(entity => entity.selected) ||
                state.data.length === 0 ||
                state.creatingData
            );
        }
    }

    async function handleSubmit() {
        if (!usedInCreateProject) {
            dispatch({
                type: TYPES.SET_CREATING_DATA,
                payload: true
            })
        }
        await onNext();
    } 

    return (
        <>
            {
                state.showNoEntitiesMessage && (
                    <h5>We could not find {ENTITY_PLURAL_LABEL.toLowerCase()} for you, but here are the most used ones.</h5>
                )
            }
            <div style={{ height: "65%" }}>
                
                    <div className='my-1 mx-2 px-1'>
                        <label className='form-label'>
                            {/* <Tooltip anchorElement="target" position="right"> */}
                                <p>
                                    Look for {ENTITY_PLURAL_LABEL.toLowerCase()}
                                    &nbsp;
                                    <span>
                                        <i
                                            className="mx-1 fal fa-info-circle"
                                            title={`
                                                ${ENTITY_PLURAL_LABEL} are what you are trying to manage in the app. For example, Account, Opportunity, and Task are objects of a Sales Management app.
                                            `} />
                                    </span>
                                </p>
                            {/* </Tooltip> */}
                        </label>
                        <div className='d-flex align-items-center'>
                            <RecommendationFilterMultiSelect
                                allowCustom={true}
                                value={customEntities}
                                onChange={handleCustomEntityChange}
                                disabled={state.loading} />
                            <button
                                style={{ width: "230px" }}
                                className='btn btn-outline-primary mx-2' 
                                onClick={refreshData}
                                disabled={customEntities.length === 0 || state.loading}
                            >
                                <i class="fa-solid fa-rotate"></i>
                                &nbsp;
                                Update Recommendation
                            </button>
                            <Dropdown
                                autoClose="outside"
                            >
                                <Dropdown.Toggle className='without-dropdown-arrow' variant='light' disabled={state.loading}>
                                    <i className="fa-solid fa-ellipsis" />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => {
                                            // dispatch action to toggle showLibraryAttributes
                                            dispatch({
                                                type: TYPES.TOGGLE_SHOW_LIBRARY_ATTRIBUTES
                                            })
                                        }}>
                                        <Checkbox
                                            label={"Only recommended"}
                                            checked={state.showLibraryAttributes}
                                            value={state.showLibraryAttributes}
                                        />
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={() => {
                                            // dispatch action to toggle showOrganizationAttributes
                                            dispatch({
                                                type: TYPES.TOGGLE_SHOW_ORGANIZATION_ATTRIBUTES
                                            })
                                        }}>
                                        <Checkbox
                                            label={"Only from my organization"}
                                            checked={state.showOrganizationAttributes}
                                            value={state.showOrganizationAttributes}
                                        />
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                    {
                        !state.loading &&
                        state.data.length === 0 ?
                        <div className='w-100'>
                            <p>
                                We seem like not have a recommendation for the {ENTITY_PLURAL_LABEL.toLowerCase()} you typed in.
                            </p>
                            <p>
                                You can type in some other {ENTITY_PLURAL_LABEL.toLowerCase()} or create your own by clicking Next.  
                            </p>
                        </div>
                        :
                        
                        <Scrollable>
                            {
                                state.loading ?
                                <Grid
                                    data={Array.from({ length: 10 }, () => ({}))}>
                                    <GridColumn
                                        width={30}
                                        cell={() => (
                                            <td>
                                                <Skeleton />
                                            </td>
                                        )} />
                                    <GridColumn
                                        cell={() => (
                                            <td>
                                                <Skeleton />
                                            </td>
                                        )} />
                                </Grid>
                                :
                                <Grid
                                    data={state.data}
                                    detail={props => (
                                        <EntityDetail
                                            attributes={props.dataItem.attributes}
                                            entityId={props.dataItem.id}
                                            requiredInAlice={props.dataItem.requiredInAlice === "true"}
                                            numRequiredAttributes={props.dataItem.numRequiredAttributes}
                                            organizationId={organizationId} />
                                    )}
                                    expandField="expanded"
                                    onExpandChange={handleExpand}
                                    scrollable="scrollable">
                                    <GridColumn
                                        width={30}
                                        headerClassName="pl-3 p-0"
                                        headerCell={() => (
                                            <>
                                                &nbsp;&nbsp;&nbsp;
                                                <Checkbox
                                                    checked={state.data.length > 0 && state.data.every(entity => entity.selected)}
                                                    indeterminate={state.data.length > 0 && state.data.some(entity => entity.selected) && !state.data.every(entity => entity.selected)}
                                                    onChange={handleSelectAll} />
                                            </>
                                        )}
                                        cell={(props) => (
                                            <td>
                                                <Checkbox
                                                    checked={props.dataItem.selected}
                                                    disabled={props.dataItem.requiredInAlice === "true"}
                                                    onChange={() => handleEntitySelect(props.dataItem)} />
                                            </td>
                                        )}
                                    />
                                    <GridColumn
                                        title={`${ENTITY_LABEL} Name`}
                                        field="name"
                                        cell={(props) => (
                                            <td onClick={(e) => handleExpand({ dataItem: props.dataItem })}>
                                                { props.dataItem[props.field] }
                                            </td>
                                        )}
                                    />
                                </Grid>
                            }

                            {
                                state.isShowMoreOptionVisible &&
                                <button type='button' onClick={getNextSetOfEntities} className="btn btn-link pointer font-weight-2 mt-2 p-0">
                                    Show More
                                </button>
                            }
                       </Scrollable>
                    }
                
            
            <DialogActionsBar layout='end'>
                { state.creatingData && (
                    <>
                        <OverlayLoader />
                        <InnerLoader borderColor="green" />
                    </>
                )}
                {
                    onBack &&
                    <button onClick={onBack} className="btn">
                        Back
                    </button>
                }
                &nbsp;
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={shouldNextBeDisabled()}
                >
                    { addButtonText }
                </button>
            </DialogActionsBar>
            </div>
        </>
    )
}

export function EntityDetail({ entityId, attributes, requiredInAlice, numRequiredAttributes }) {

    const { state, dispatch } = useContext(EntityRecommendationContext);

    const tooltip = useRef(null);

    debugger;

    const handleSelect = (attribute) => {
        const { id, selected } = attribute;
        const type = selected ? TYPES.ATTRIBUTE_DESELECTED : TYPES.ATTRIBUTE_SELECTED;
        dispatch({
            type,
            payload: { entityId, attributeId: id }
        })
    }

    function getAttributesToShow() {
        if (state.showLibraryAttributes && state.showOrganizationAttributes) {
            return attributes;
        } else if (state.showLibraryAttributes) {
            return attributes.filter(attribute => attribute.fromLibrary);
        } else if (state.showOrganizationAttributes) {
            return attributes.filter(attribute => !attribute.fromLibrary);
        } else {
            return [];
        }
    }

    function shouldCheckboxBeDisabled(attribute, index) {
        if (attribute.requiredInAlice === "true") {
            return true;
        }
        return requiredInAlice && numRequiredAttributes === 0 && index === 0;
    }

    return (
        <tr className="k-detail-row w-100 border-0">
            <td className="k-hierarchy-cell"></td>
            <td className="k-detail-cell w-100 border-0 recommendLabel">
                <Tooltip
                    ref={(value) => {
                        tooltip.current = value;
                    }}
                    anchorElement='target'
                    position='right' />
                <div className="row">
                    {
                        getAttributesToShow().map((attribute, index) => (
                            <div className="col-2 py-2" key={attribute.id}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={attribute.selected}
                                        onChange={() => handleSelect(attribute)}
                                        disabled={shouldCheckboxBeDisabled(attribute, index)} />
                                    <span className="ml-2 d-flex">
                                        <div
                                            className='px-2'
                                            title="You can group properties under different sections when showing in the Form page of your app."
                                            onMouseEnter={(event) => {
                                                tooltip.current.handleMouseOver(event)
                                            }}
                                            onMouseLeave={(event) => {
                                                tooltip.current.handleMouseOut(event)
                                            }}
                                        >
                                            <AttributeIcon attributeType={attribute.attributeType} />
                                        </div>
                                        {attribute.attributeName}
                                    </span>
                                </label>
                                    {/* <Checkbox
                                        label={
                                            <div className='d-flex'>
                                                <div
                                                    title="You can group properties under different sections when showing in the Form page of your app.">
                                                    <AttributeIcon attributeType={attribute.attributeType} />
                                                </div>
                                                <span>{attribute.attributeName}</span>
                                            </div>
                                        }
                                        checked={attribute.selected}
                                        onChange={() => handleSelect(attribute)}
                                        disabled={shouldCheckboxBeDisabled(attribute, index)} /> */}
                            </div>
                        ))
                    }
                </div>
            </td>
        </tr>
    )
}

export function RecommendedLibraryProjectsWithActions({ organizationId, projectId, tags, onAppImported, onNext, onBack }) {

    const msalContext = useMsal();

    async function importSchemaFromProject(solution) {
        try {
            const email = msalContext.accounts[0].username;
            const request = await callApiWithAuthorization("/api/v0/project/"+projectId+"/import/"+solution.id, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({
                    organizationId, email
                })
            });
            const response = await request.json();
            onAppImported(response);
        } catch (e) {
            console.error(e);
        }
    }

    return (
        <>
            <div style={{ height: "90%" }}>
                <Scrollable>
                    <RecommendedLibraryProjects 
                        organizationId={organizationId}
                        projectId={projectId}
                        tags={tags}
                        importApp={importSchemaFromProject}
                    />
                </Scrollable>
            </div>
            <DialogActionsBar layout="end">
                <button onClick={onBack} className="btn">Back</button>
                <button onClick={onNext} className="btn btn-primary">Continue to build your own</button>
            </DialogActionsBar>
        </>
    )
}

RecommendedEntityGrid.defaultProps = {
    entities: []
}

ProjectType.defaultProps = {
    types: []
}

export default EntityRecommendation;